import { LitElement, html, utils } from "./-global.min.js"

const { addToCart, updateQuantity, reRenderShopifySections } = utils

class HHGiftNote extends LitElement {
  connectedCallback() {
    super.connectedCallback()

    this.toggle = this.querySelector(".gift-note__toggle")
    this.textarea = this.querySelector(".gift-note__textarea")
    this.accordion = this.querySelector("hh-accordion")

    this.variantId = Number(this.dataset.variantId)
    this.sections = (this.dataset.sections || "")
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean)

    // mini-cart host (persists across inner re-renders)
    this.miniCart =
      this.closest("hh-mini-cart") || document.querySelector("hh-mini-cart")

    // Prevent accidental form submit from the summary button
    this.addEventListener("click", (e) => {
      const btn = e.target.closest("button.accordion-summary")
      if (btn && btn.type !== "button") btn.type = "button"
    })

    this.toggle?.addEventListener("change", () => this.onToggle())

    this.syncFromCart()
  }

  render() {
    return html`<slot></slot>`
  }

  // ---- loading helper ----
  setLoading(state) {
    if (!this.miniCart) return
    this.miniCart.setAttribute("loading", state ? "true" : "false")
  }

  // ---- Accordion helpers (mirror component-accordion behavior) ----
  _getAccParts(acc = this.accordion) {
    if (!acc?.shadowRoot) return {}
    const slot = acc.shadowRoot.querySelector("slot")
    const assigned = slot?.assignedElements() || []
    return { summary: assigned[0], content: assigned[1] }
  }
  _openAccordion(acc = this.accordion) {
    const { summary, content } = this._getAccParts(acc)
    if (!summary || !content) return
    summary.classList.add("active")
    content.style.maxHeight = `${content.scrollHeight}px`
  }
  _closeAccordion(acc = this.accordion) {
    const { summary, content } = this._getAccParts(acc)
    if (!summary || !content) return
    summary.classList.remove("active")
    content.style.maxHeight = null
  }
  _syncAccordion() {
    if (!this.accordion || !this.toggle) return
    if (this.toggle.checked) this._openAccordion()
    else this._closeAccordion()
  }

  // ---- Initial sync from cart (after refresh) ----
  async syncFromCart() {
    const cart = await this.getCart()
    const inCart = cart.items.some(
      (i) => Number(i.variant_id) === this.variantId
    )

    if (this.toggle) this.toggle.checked = inCart
    if (this.textarea) {
      this.textarea.disabled = !inCart
      this.textarea.required = inCart
    }

    this._syncAccordion()
  }

  // ---- Toggle add/remove product with loading indicator ----
  async onToggle() {
    const checked = this.toggle.checked
    const currentNote = this.textarea?.value ?? ""

    try {
      this.setLoading(true)

      if (checked) {
        const json = await addToCart(
          {
            items: [
              {
                id: this.variantId,
                quantity: 1,
                properties: { _gift_note_product: "true" }, // hidden flag
              },
            ],
          },
          this.sections.join(",")
        )
        this._rerenderAndRestore(json, currentNote)
      } else {
        const cart = await this.getCart()
        const idx = cart.items.findIndex(
          (i) => Number(i.variant_id) === this.variantId
        )
        if (idx !== -1) {
          const json = await updateQuantity(idx + 1, 0, this.sections.join(","))
          this._rerenderAndRestore(json, currentNote)
        } else {
          // nothing to remove; just resync UI
          this._syncAccordion()
        }
      }

      if (this.textarea) this.textarea.disabled = !checked
      this._syncAccordion()
    } finally {
      // turn off loading after the DOM has been updated/restored
      queueMicrotask(() => this.setLoading(false))
    }
  }

  // ---- Fetch cart ----
  async getCart() {
    const res = await fetch("/cart.js", {
      headers: { Accept: "application/json" },
    })
    return res.ok ? res.json() : { items: [], note: "" }
  }

  // ---- Re-render sections and restore note + accordion state ----
  _rerenderAndRestore(json, restoreNote) {
    if (reRenderShopifySections) {
      reRenderShopifySections(json)
    } else {
      const map = json?.sections || {}
      this.sections.forEach((id) => {
        const html = map[id]
        if (!html) return
        const container = document.getElementById(`shopify-section-${id}`)
        if (container) container.innerHTML = html
      })
    }

    // After Shopify swaps the section DOM, re-select inside the new tree
    queueMicrotask(() => {
      const host = document.querySelector("hh-gift-note")
      if (!host) return
      const ta = host.querySelector(".gift-note__textarea")
      const toggle = host.querySelector(".gift-note__toggle")
      const acc = host.querySelector("hh-accordion")

      if (ta && typeof restoreNote === "string") ta.value = restoreNote
      if (ta && toggle) ta.disabled = !toggle.checked

      // open/close via class + maxHeight (matches accordion’s internal logic)
      if (acc && toggle) {
        const slot = acc.shadowRoot?.querySelector("slot")
        const assigned = slot?.assignedElements() || []
        const summary = assigned[0]
        const content = assigned[1]
        if (summary && content) {
          const open = toggle.checked
          summary.classList.toggle("active", open)
          content.style.maxHeight = open ? `${content.scrollHeight}px` : null
        }
      }
    })
  }
}

customElements.define("hh-gift-note", HHGiftNote)
